import {
	ReadingQueue,
	createNlsMiddleware,
	keymap,
	lccstate,
	pathmap,
	readingTopicQueueChange,
	userstate
} from 'lcc-state';
import { isEmpty } from 'lodash-es';

import has from '@dojo/framework/core/has';
import { WidgetProperties } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { Store } from '@dojo/framework/stores/Store';
import { Button } from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import { convertToBoolean } from '@holmescorp/common';
import { logQuiz } from '@holmescorp/logger';
import { numeral } from '@holmescorp/numeral';
import { publish } from '@holmescorp/pub-sub';
import { ScoreChart } from '@holmescorp/progress-chart';

import css from '../../css/questionfeedback.m.css';
import { QuestionQueueType, QuizMeta, QuizRPC } from '../../interfaces';
import { BaseProperties, createProgressionMiddleware } from '../../middleware/progression';
import { svc } from '../../support/qfsvc';
import { QuestionQueue } from '../../support/questionqueue';
import { ScoreRollup } from '../../support/score-rollup';
import { quizstate, setInreview, setIsAutoStart, setIsaudit } from '../../support/state';
import { QuizEndModal } from '../../widgets/quizend-modal';

interface ReadingReference {
	domain?: string;
	topicset?: string;
	topicgroup?: string;
	topic?: string;
}

interface RecommendedReading {
	instructions_reading?: string;
	references: ReadingReference[];
}

interface DefaultQuestionFeedbackIcache {
	inited: boolean;
	started: boolean;
	calledReadingOnBegin: boolean;
	calledReadingOnEnd: boolean;
	reading: RecommendedReading;
}

export interface DefaultQuestionFeedbackProperties {
	quizstore: Store<QuizMeta>;
}

interface AllProps extends BaseProperties, DefaultQuestionFeedbackProperties, WidgetProperties {
	question: any;
}

const nls = createNlsMiddleware('quiz');
const progression = createProgressionMiddleware();
const icache = createICacheMiddleware<DefaultQuestionFeedbackIcache>();
const factory = create({
	icache,
	nls,
	theme,
	progression,
	quizstate,
	userstate,
	lccstate
}).properties<DefaultQuestionFeedbackProperties>();

export const DefaultQuestionFeedback = factory(
	({ id, properties, middleware: { icache, nls, theme, progression, quizstate, userstate, lccstate } }) => {
		icache.getOrSet('inited', () => {
			const { quizstore } = properties();
			quizstate.setStore(quizstore);
			progression.setStore(quizstore);
			progression.initialize();
			progression.svc = svc;
			return true;
		});

		const props = () => {
			const { get, path } = quizstate;
			const theProps: Partial<AllProps> = Object.assign({}, progression.getProperties(), properties(), {
				question: get(path('question'))
			});
			return theProps as AllProps;
		};

		const themeCss = theme.classes(css);
		const { get: uget, path: upath } = userstate;

		const tracking = lccstate.get(lccstate.path('tracking'));
		const { topicset = '', topicgroup = '' } = tracking ? tracking : {};
		const chkForQuiz = new RegExp('quiz|((calculation|exercise)-\\d+)');
		const isTopicset = chkForQuiz.test(topicgroup);
		const chkForActivity = new RegExp('((calculation|exercise)-\\d+)');
		const isActivity = chkForActivity.test(topicgroup);
		const topickey = isTopicset ? topicset : topicgroup;

		const startQuiz = async (quizid: string) => {
			logQuiz.info(`begin quiz ${quizid}`);

			quizstate.run(setIsaudit)({ isaudit: false });
			quizstate.run(setInreview)({ inreview: false });

			const v = await svc.start_quiz();
			progression.startquiz = v;
			progression.quiz_startup();
		};

		const get_recommended_reading = async (resetQueue = false) => {
			const readingTool = uget(upath('toolstate', 'reading'));
			const readingQueue: ReadingQueue = readingTool.topic_queues[topickey];
			const isRecommendedReadingComplete = readingQueue && readingQueue.complete;

			if (isRecommendedReadingComplete && !resetQueue) {
				return;
			}

			const recommendations = await progression.svc.recommended_reading();
			const topics: string[] = recommendations.references.map((ref: ReadingReference) => {
				const { topicgroup = '', topic = '' } = ref;
				return topic || topicgroup;
			});

			const data: ReadingQueue = { cursor: 0, topics, complete: false };
			readingTopicQueueChange({ key: topickey, data });
			return recommendations;
		};

		const display_recommended_reading = () => {
			const {
				messages: { recommended_reading_title = '[quiz] recommended_reading_title' }
			} = nls();

			const topicdata = icache.get('reading')?.references.map((ref: ReadingReference) => {
				const { topicgroup = '', topic = '' } = ref;
				const selected = topic || topicgroup;
				return keymap.get(selected);
			});

			const topic_list = topicdata?.map((topic) => {
				return v('li', {}, [topic?.title]);
			});
			return !icache.get('reading')?.instructions_reading && topic_list?.length && !isTopicset
				? v('div', { key: 'recommended-reading', classes: [themeCss.recommendedReading] }, [
						v('p', {}, [recommended_reading_title]),

						v('ul', {}, topic_list)
				  ])
				: v('div', {
						key: 'recommended-reading',
						classes: [themeCss.recommendedReading],
						innerHTML: icache.get('reading')?.instructions_reading
				  });
		};

		const buttons = () => {
			const {
				messages: {
					continue: continueText = 'Continue',
					start_over: restartText = 'Start Over',
					begin: beginText = 'Begin',
					review_last_attempt: reviewText = '',
					revisit: retakeText = 'Re-take Quiz',
					hide_begin_on_quiz_end = 'none'
				}
			} = nls();
			const { quiz = {} as QuizRPC, info, isrunning } = props();
			const { use_restart } = quiz;
			const { svc } = progression;
			const quizid = quiz.id;
			const noBeginOnFinal = isrunning === 'final' && hide_begin_on_quiz_end !== 'none';

			if (use_restart) {
				return [
					v('div', { xqa: 'action restart', classes: [themeCss.oneaction] }, [
						w(
							Button,
							{
								key: 'restart',
								onClick: async () => {
									logQuiz.info(`restart quiz ${quizid}`);
									quizstate.run(setIsaudit)({ isaudit: false });
									quizstate.run(setInreview)({ inreview: false });

									const v = await svc.restart_quiz();
									progression.startquiz = v;
									progression.quiz_startup(true);
								}
							},
							[
								v('span', {}, [
									v('i', { classes: ['fas', 'fa-reply', themeCss.actionIcon] }),
									restartText
								])
							]
						)
					]),
					v('div', { xqa: 'action continue', classes: [themeCss.oneaction, themeCss.primaryButton] }, [
						w(
							Button,
							{
								key: 'continue',

								onClick: async () => {
									logQuiz.info(`continue quiz ${quizid}`);
									quizstate.run(setIsaudit)({ isaudit: false });
									quizstate.run(setInreview)({ inreview: false });

									const v = await svc.continue_quiz();
									progression.startquiz = v;
									progression.quiz_startup();
								}
							},
							[
								v('span', {}, [
									continueText,
									v('i', { classes: ['fas', 'fa-arrow-right', themeCss.actionIcon] })
								])
							]
						)
					])
				];
			} else {
				const lastresult = info && info.lastresult;
				const hasLastresult = lastresult && !isEmpty(lastresult);
				return [
					noBeginOnFinal
						? null
						: v('div', { xqa: 'action begin', classes: [themeCss.oneaction, themeCss.primaryButton] }, [
								w(
									Button,
									{
										key: 'begin',
										onClick: () => startQuiz(quizid)
									},
									[
										hasLastresult
											? retakeText
											: v('span', {}, [
													beginText,
													v('i', { classes: ['fas', 'fa-arrow-right', themeCss.actionIcon] })
											  ])
									]
								)
						  ]),
					hasLastresult && !quiz.attempts_noaudit
						? v('div', { xqa: 'action audit', classes: [themeCss.oneaction, themeCss.primaryButton] }, [
								w(
									Button,
									{
										key: 'audit',
										onClick: async () => {
											progression.audit_init();

											logQuiz.info(`audit quiz ${quizid}`);

											quizstate.run(setIsaudit)({ isaudit: true });
											quizstate.run(setInreview)({ inreview: false });

											await progression.auditsvc.start_quiz();

											progression.startquiz = { cursor: 0, status: 'feedback' };
											progression.audit_startup();
										}
									},
									[reviewText]
								)
						  ])
						: null
				];
			}
		};

		const section_buttons = () => {
			const {
				messages: {
					recommendations_button_title = '[quiz] recommendations_button_title',
					next_topic_button_title = '[quiz] next_topic_button_title',
					topicset_quiz_button_title = '[quiz] topicset_quiz_button_title',
					studyplan_button_title = '[quiz] studyplan_button_title',
					retake: restartText = 'Retake'
				}
			} = nls();
			const status = uget(upath('status'));
			const { next = '', parent = '' } = status[topickey] ? status[topickey] : {};
			// only enforce recommendations for non-topicset quizzes
			const recommendations = !isTopicset && icache.get('reading') && icache.get('reading')?.references;

			return [
				!recommendations || !recommendations.length
					? isTopicset
						? isActivity
							? v('div', { xqa: 'action retake', classes: [themeCss.oneaction] }, [
									w(
										Button,
										{
											key: 'retake',
											onClick: async () => {
												quizstate.run(setIsaudit)({ isaudit: false });
												quizstate.run(setInreview)({ inreview: false });

												const v = await svc.restart_quiz();
												progression.startquiz = v;
												progression.quiz_startup(true);
											}
										},
										[restartText]
									)
							  ])
							: v('div', { xqa: 'action studyplan', classes: [themeCss.oneaction] }, [
									w(
										Button,
										{
											key: 'studyplan-button',
											onClick: () => {
												logQuiz.info(`go to studyplan`);
												publish('mystudyplan/view');
											}
										},
										[studyplan_button_title]
									)
							  ])
						: next
						? v('div', { xqa: 'action next-topic', classes: [themeCss.oneaction] }, [
								w(
									Button,
									{
										key: 'next-topic',
										onClick: () => {
											logQuiz.info(`go to ${next}`);
											const { path } = keymap.get(next)!;
											const { view } = pathmap.get(path)!;
											view();
										}
									},
									[next_topic_button_title]
								)
						  ])
						: v('div', { xqa: 'action topicset-quiz', classes: [themeCss.oneaction] }, [
								w(
									Button,
									{
										key: 'tgf-quiz',
										onClick: () => {
											logQuiz.info(`go to ${parent} quiz`);
											const { path } = keymap.get(parent)!;
											const { view } = pathmap.get(`${path}/quiz`)!;
											view();
										}
									},
									[topicset_quiz_button_title]
								)
						  ])
					: v('div', { xqa: 'action recommendations', classes: [themeCss.oneaction] }, [
							w(
								Button,
								{
									key: 'recommendations',
									onClick: () => {
										const firstReading = recommendations[0];
										const topic = firstReading.topic || firstReading.topicgroup;
										const { path } = keymap.get(topic!)!;
										const { view } = pathmap.get(path)!;
										view();
									}
								},
								[recommendations_button_title]
							)
					  ])
			];
		};

		const render_micro_view = () => {
			const { quiz = {} as QuizRPC, info, isrunning } = props();

			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const { instructions_done, instructions } = quiz;

			const bestScore = parseInt(info?.bestscore || '-1');

			return [
				v('div', { classes: [themeCss.microQuizWrapper], xqa: 'micro-quiz-wrapper' }, [
					v('div', { classes: [themeCss.microQuizHeader] }, [
						v('div', {
							key: 'instructions',
							id: 'quiz-instructions',
							classes: [themeCss.instructions],
							innerHTML: isrunning === 'final' ? instructions_done : instructions
						}),
						hasLastresult
							? v('div', { key: 'score', classes: [themeCss.score], xqa: 'last-quiz-score' }, [
									w(ScoreChart, {
										score: lastresult?.totalpercentcorrect || -1,
										bestScore,
										bestScoreText: 'Best Test Score',
										insideLabelText: 'Test Score'
									})
							  ])
							: v('div', { key: 'score', xqa: 'last-quiz-score' })
					]),
					v(
						'div',
						{
							key: 'quiz-actions',
							classes: [themeCss.actions],
							role: 'group',
							...formatAriaProperties({ labelledby: 'quiz-instructions' })
						},
						[
							...buttons(),
							v('div', { xqa: 'action home', classes: [themeCss.oneaction, themeCss.primaryButton] }, [
								w(
									Button,
									{
										key: 'home',
										onClick: () => publish('site/homepage'),
										onDown: () => publish('site/homepage')
									},
									[
										v('span', {}, [
											v('i', { classes: ['fas', 'fa-home', themeCss.actionIcon] }),
											'Home'
										])
									]
								)
							])
						]
					)
				])
			];
		};

		const run_quiz_end = () => {
			if (has('use-micro-view')) {
				return render_micro_view();
			}

			const {
				messages: { score: scoreText = '', use_quiz_end_modal = 'false', quiz_end_no_recommendations = '' }
			} = nls();
			const { quiz = {} as QuizRPC, info } = props();

			const isSectionQuiz = quiz.kind === 'section' && has('use-study-plan');
			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const score = hasLastresult ? lastresult!.totalpercentcorrect : 0;
			const percent = score * 0.01;
			const { instructions_done } = quiz;

			const uqem = convertToBoolean(use_quiz_end_modal);

			const instructions =
				icache.get('reading')?.references.length == 0 && isSectionQuiz
					? quiz_end_no_recommendations
					: instructions_done;

			// recommendations only apply to TG quizzes
			if (!isTopicset && !icache.get('calledReadingOnEnd')) {
				icache.set('calledReadingOnEnd', true);

				icache.set('reading', () => get_recommended_reading(true));
			}

			return [
				v('div', {
					key: 'instructions',
					id: 'quiz-instructions',
					classes: [themeCss.instructions],
					innerHTML: instructions
				}),
				hasLastresult && !quiz.attempts_noscore
					? v('div', { key: 'score', classes: [themeCss.score], xqa: 'last-quiz-score' }, [
							v('span', { classes: [themeCss.scoreTitle] }, [`${scoreText} `]),
							v('span', { classes: [themeCss.scorePercent] }, [`${numeral(percent).format('0%')}`])
					  ])
					: v('div', { key: 'score', xqa: 'last-quiz-score' }),

				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [themeCss.actions],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					isSectionQuiz ? section_buttons() : buttons()
				),
				hasLastresult && icache.get('reading') ? display_recommended_reading() : null,
				uqem ? w(QuizEndModal, { score }) : null
			];
		};

		const run_quiz_begin = () => {
			if (has('use-micro-view')) {
				return render_micro_view();
			}

			const {
				messages: { last_score: lastScoreText = '', reading_queue_prompt = '', use_quiz_autostart = 'false' }
			} = nls();
			const { quiz = {} as QuizRPC, info } = props();
			const isSectionQuiz = quiz.kind === 'section' && has('use-study-plan');
			const autostart = convertToBoolean(use_quiz_autostart);
			const isStarted = quizstate.get(quizstate.path('isautostart'));

			// jump right into the quiz if setting is on OR by default for TG quizzes
			if (autostart || (!isTopicset && isSectionQuiz)) {
				if (isStarted) {
					return [];
				}

				startQuiz(quiz.id);
				quizstate.run(setIsAutoStart)({ isautostart: true });
				return [];
			}

			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const percent = hasLastresult ? lastresult!.totalpercentcorrect * 0.01 : 0;
			const { instructions: defaultInstructions } = quiz;

			if (!isTopicset && !icache.get('calledReadingOnBegin')) {
				icache.set('calledReadingOnBegin', true);

				icache.set('reading', () => get_recommended_reading());
			}

			const instructions =
				icache.get('reading')?.references.length && isSectionQuiz ? reading_queue_prompt : defaultInstructions;

			return [
				v('div', {
					key: 'instructions',
					id: 'quiz-instructions',
					classes: [themeCss.instructions],
					innerHTML: instructions
				}),
				hasLastresult && !quiz.attempts_noscore
					? v('div', { key: 'score', classes: [themeCss.score], xqa: 'last-quiz-score' }, [
							v('span', { classes: [themeCss.scoreTitle] }, [`${lastScoreText} `]),
							v('span', { classes: [themeCss.scorePercent] }, [`${numeral(percent).format('0%')}`])
					  ])
					: v('div', { key: 'score', xqa: 'last-quiz-score' }),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [themeCss.actions],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					hasLastresult && icache.get('reading') && isSectionQuiz ? section_buttons() : buttons()
				),
				hasLastresult && icache.get('reading') ? display_recommended_reading() : null
			];
		};

		const render_page = () => {
			const { isPlaceholder } = nls();
			if (isPlaceholder) {
				return [];
			}
			const { isrunning, instancekey = '' } = props();
			const { finalize, startquiz, svc, run_audit } = progression;
			const quizstore = quizstate.getStore();

			switch (isrunning) {
				case 'quiz':
					return [
						w<QuestionQueueType>(QuestionQueue, {
							key: `${instancekey}-queue`,
							svc,
							finalize,
							startquiz,
							quizstore
						})
					];

				case 'audit':
					return run_audit();

				case 'final':
					return run_quiz_end();

				default:
					return run_quiz_begin();
			}
		};

		const { quiz, info } = props();
		const hasdata = quiz && info;
		!hasdata && progression.load_quiz();

		return v(
			'div',
			{ id, xqa: `progression question-feedback` },
			hasdata
				? [
						w(
							ScoreRollup,
							{ key: `rollup-${id}`, quizstore: quizstate.getStore(), includeParents: false } as any,
							render_page()
						)
				  ]
				: progression.render_loading()
		);
	}
);

export default DefaultQuestionFeedback;
